import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import contrastColor from "../../helpers/contrastColor";
import { isDarkTheme } from "../../helpers/darkTint";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Box,
} from "@material-ui/core";
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  GetApp as GetAppIcon,
} from "@material-ui/icons";
import { format } from "date-fns";
import { exportToExcel } from "../../utils/exportToExcel";
import { ptBR } from "date-fns/locale";

// "Coluna" is always shown; "Título" is always shown.
// These are the toggleable fields (same as ColumnReportModal).
const TOGGLEABLE_FIELDS = [
  { key: "description", label: "Descrição" },
  { key: "priority", label: "Prioridade" },
  { key: "dueDate", label: "Vencimento" },
  { key: "assignedUser", label: "Responsável" },
  { key: "cliente", label: "Cliente" },
  { key: "valorReais", label: "Valor R$" },
  { key: "tags", label: "Tags" },
  { key: "tasks", label: "Tarefas" },
  { key: "createdAt", label: "Criado em" },
];

const KNOWN_CF_KEYS = new Set([
  "title",
  "description",
  "priority",
  "dueDate",
  "headerColor",
  "valorReais",
]);

const PRIORITY_LABELS = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const PRIORITY_COLORS = {
  low: "#3498db",
  medium: "#f39c12",
  high: "#e67e22",
  urgent: "#e74c3c",
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    const date =
      typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)
        ? new Date(`${d}T00:00:00`)
        : new Date(d);
    if (isNaN(date.getTime())) return "—";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
};

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return "—";
    return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
};

const formatCurrency = (v) => {
  if (v == null || v === "") return "—";
  try {
    return Number(v).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  } catch {
    return String(v);
  }
};

const useStyles = makeStyles((theme) => ({
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: theme.spacing(0.5),
  },
  reportTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  // Fundos claros fixos deixavam o texto branco do tema escuro ilegível;
  // na impressão o papel é branco, então volta às cores claras.
  th: {
    padding: "8px 10px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f0f3f9",
    fontWeight: 700,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#ddd"}`,
    textAlign: "left",
    whiteSpace: "nowrap",
    "@media print": {
      backgroundColor: "#f0f3f9",
      borderColor: "#ddd",
    },
  },
  td: {
    padding: "7px 10px",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#ddd"}`,
    verticalAlign: "top",
    "@media print": {
      borderColor: "#ddd",
    },
  },
  trEven: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#fafafa",
    "@media print": {
      backgroundColor: "#fafafa",
    },
  },
  priorityBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
  },
  columnBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    backgroundColor: theme.palette.type === "dark" ? "rgba(159,168,218,0.16)" : "#e8eaf6",
    color: theme.palette.type === "dark" ? "#9fa8da" : "#3949ab",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    "@media print": {
      backgroundColor: "#e8eaf6",
      color: "#3949ab",
    },
  },
  printOnlyHeader: {
    display: "none",
  },
}));

const getCellValue = (card, field) => {
  const priority = card.priority || card.customFields?.priority || null;
  const dueDate = card.dueDate || card.customFields?.dueDate || null;
  const assignedUser = card.assignedUser || null;
  const cliente = card.cliente || null;
  const valorReais = card.customFields?.valorReais || null;
  const tags = card.tags || [];
  const tasks = card.tasks || [];
  const createdAt = card.createdAt || null;

  switch (field.key) {
    case "description":
      return card.customFields?.description || card.description || "—";
    case "priority":
      return priority ? PRIORITY_LABELS[priority] || priority : "—";
    case "dueDate":
      return formatDate(dueDate);
    case "assignedUser":
      return assignedUser?.name || "—";
    case "cliente":
      return cliente?.NOMEFANTASIA || cliente?.NOMECLI || cliente?.name || cliente?.nome || "—";
    case "valorReais":
      return formatCurrency(valorReais);
    case "tags":
      return Array.isArray(tags) && tags.length > 0 ? tags.join(", ") : "—";
    case "tasks": {
      if (!tasks.length) return "—";
      const done = tasks.filter((t) => t.completed).length;
      return `${done}/${tasks.length}`;
    }
    case "createdAt":
      return formatDateTime(createdAt);
    default:
      if (field.isCustom) {
        const val = card.customFields?.[field.rawKey];
        return val != null && val !== "" ? String(val) : "—";
      }
      return "—";
  }
};

// Flatten all cards from all columns, attaching columnName and columnColor
const buildFlatCards = (board) => {
  const result = [];
  const columns = [...(board?.columns || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  columns.forEach((col) => {
    (col.cards || []).forEach((card) => {
      result.push({ ...card, _columnName: col.name, _columnColor: col.color });
    });
  });
  return result;
};

const BoardReportModal = ({ open, onClose, board }) => {
  const classes = useStyles();
  const theme = useTheme();
  // Coluna com cor escura (ex.: #113131) some como texto sobre o fundo escuro
  const isDarkInk = (color) => isDarkTheme(theme) && contrastColor(color) === "#ffffff";
  const [step, setStep] = useState("select");
  const [selectedFields, setSelectedFields] = useState([]);

  const boardId = board?.id;

  const customFieldDefs = useMemo(() => {
    const columns = board?.columns || [];
    const keys = new Set();
    columns.forEach((col) => {
      (col.cards || []).forEach((card) => {
        if (card.customFields && typeof card.customFields === "object") {
          Object.keys(card.customFields).forEach((k) => {
            if (!KNOWN_CF_KEYS.has(k)) keys.add(k);
          });
        }
      });
    });
    return Array.from(keys).map((k) => ({
      key: `cf_${k}`,
      label: k,
      rawKey: k,
      isCustom: true,
    }));
  }, [board?.columns]);

  const allToggleableFields = useMemo(
    () => [...TOGGLEABLE_FIELDS, ...customFieldDefs],
    [customFieldDefs]
  );

  useEffect(() => {
    if (!open) return;
    setStep("select");
    try {
      const saved = localStorage.getItem(`kanban_board_report_fields_${boardId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const available = new Set(allToggleableFields.map((f) => f.key));
          setSelectedFields(parsed.filter((k) => available.has(k)));
          return;
        }
      }
    } catch {
      // ignore
    }
    setSelectedFields(allToggleableFields.map((f) => f.key));
  }, [open, boardId, allToggleableFields]);

  const handleToggle = (key) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleGenerate = () => {
    localStorage.setItem(
      `kanban_board_report_fields_${boardId}`,
      JSON.stringify(selectedFields)
    );
    setStep("report");
  };

  const handlePrint = () => {
    const style = document.createElement("style");
    style.id = "kanban-board-report-print-style";
    style.innerHTML = `
      @media print {
        body > * { visibility: hidden !important; }
        #kanban-board-report-print-area { visibility: visible !important; position: fixed; left: 0; top: 0; width: 100%; background: white; color: #000; padding: 16px; box-sizing: border-box; }
        #kanban-board-report-print-area * { visibility: visible !important; }
        #kanban-board-report-print-area .print-only-header { display: block !important; }
        #kanban-board-report-print-area .column-badge-ink { color: #000 !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    document.head.removeChild(style);
  };

  const handleExportExcel = () => {
    const rows = flatCards.map((card, idx) => {
      const title = card.customFields?.title || card.title || "—";
      const row = {
        "#": idx + 1,
        Coluna: card._columnName || "—",
        Título: title,
      };
      visibleFields.forEach((field) => {
        row[field.label] = getCellValue(card, field);
      });
      return row;
    });
    exportToExcel(rows, `relatorio-${board?.name || "board"}`, "Cards");
  };

  // Always-visible columns: # | Coluna | Título | ...selected fields
  const visibleFields = useMemo(
    () => allToggleableFields.filter((f) => selectedFields.includes(f.key)),
    [allToggleableFields, selectedFields]
  );

  const flatCards = useMemo(() => buildFlatCards(board), [board]);
  const totalCards = flatCards.length;

  if (step === "select") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography>
          <Typography variant="h6">Selecionar Campos do Relatório</Typography>
          <Typography variant="body2" color="textSecondary">
            Board: <strong>{board?.name}</strong> · {totalCards} card
            {totalCards !== 1 ? "s" : ""} em todas as colunas
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.sectionLabel}>Campos padrão</Typography>
          <FormGroup className={classes.fieldGrid}>
            {TOGGLEABLE_FIELDS.map((field) => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleToggle(field.key)}
                    color="primary"
                    size="small"
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
          {customFieldDefs.length > 0 && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Typography className={classes.sectionLabel}>
                Campos personalizados
              </Typography>
              <FormGroup className={classes.fieldGrid}>
                {customFieldDefs.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(field.key)}
                        onChange={() => handleToggle(field.key)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleGenerate} variant="contained" color="primary">
            Gerar Relatório
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { maxHeight: "90vh" } }}
    >
      <DialogTitle disableTypography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <Tooltip title="Voltar à seleção de campos">
              <IconButton
                size="small"
                onClick={() => setStep("select")}
                style={{ marginRight: 8 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography variant="h6">
                Relatório — {board?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {totalCards} card{totalCards !== 1 ? "s" : ""} em todas as colunas
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center">
            <Tooltip title="Exportar Excel">
              <IconButton onClick={handleExportExcel}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ padding: 0 }}>
        <div id="kanban-board-report-print-area" style={{ padding: 16 }}>
          <div
            className={`print-only-header ${classes.printOnlyHeader}`}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>
              Relatório — {board?.name}
            </h2>
            <p style={{ margin: "4px 0 12px 0", fontSize: 12 }}>
              Gerado em: {formatDateTime(new Date().toISOString())} ·{" "}
              {totalCards} card{totalCards !== 1 ? "s" : ""}
            </p>
          </div>
          {totalCards === 0 ? (
            <Typography
              color="textSecondary"
              align="center"
              style={{ padding: 24 }}
            >
              Nenhum card neste board.
            </Typography>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className={classes.reportTable}>
                <thead>
                  <tr>
                    <th
                      className={classes.th}
                      style={{ width: 36, textAlign: "center" }}
                    >
                      #
                    </th>
                    <th className={classes.th}>Coluna</th>
                    <th className={classes.th}>Título</th>
                    {visibleFields.map((f) => (
                      <th key={f.key} className={classes.th}>
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flatCards.map((card, idx) => {
                    const priority =
                      card.priority || card.customFields?.priority || null;
                    const title =
                      card.customFields?.title || card.title || "—";
                    return (
                      <tr
                        key={`${card.id}-${idx}`}
                        className={idx % 2 === 1 ? classes.trEven : ""}
                      >
                        <td
                          className={classes.td}
                          style={{
                            textAlign: "center",
                            color: "#aaa",
                            fontSize: 11,
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td className={classes.td}>
                          <span
                            className={`${classes.columnBadge} ${
                              card._columnColor && isDarkInk(card._columnColor) ? "column-badge-ink" : ""
                            }`}
                            style={
                              card._columnColor
                                ? {
                                    backgroundColor: `${card._columnColor}22`,
                                    color: isDarkInk(card._columnColor)
                                      ? theme.palette.text.primary
                                      : card._columnColor,
                                  }
                                : {}
                            }
                          >
                            {card._columnName}
                          </span>
                        </td>
                        <td className={classes.td}>{title}</td>
                        {visibleFields.map((field) => (
                          <td key={field.key} className={classes.td}>
                            {field.key === "priority" && priority ? (
                              <span
                                className={classes.priorityBadge}
                                style={{
                                  backgroundColor:
                                    PRIORITY_COLORS[priority] || "#aaa",
                                }}
                              >
                                {getCellValue(card, field)}
                              </span>
                            ) : (
                              getCellValue(card, field)
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExportExcel}
        >
          Exportar Excel
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardReportModal;
